import React, { useEffect, useSyncExternalStore } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  acknowledgePlatformPendingCommands,
  checkPlatformAvailability,
  getPlatformAvailabilitySnapshot,
  startPlatformAvailabilityMonitoring,
  subscribePlatformAvailability,
} from '../services/platformAvailability.ts';

const ACTION_CONTROL = 'button, a, [role="button"], [role="menuitem"], input[type="submit"], input[type="button"], input[type="reset"]';
const NOTICE_CONTROL = '[data-platform-availability-notice]';

/** Impede efeitos locais anteriores ao HTTP, sem guardar ou repetir interações. */
function startPlatformInteractionGuard(): () => void {
  const pausedTarget = (event: Event): Element | null => {
    if (getPlatformAvailabilitySnapshot().ready) return null;
    const target = event.target instanceof Element
      ? event.target
      : event.target instanceof Node ? event.target.parentElement : null;
    return target && !target.closest(NOTICE_CONTROL) ? target : null;
  };
  const stopAction = (event: Event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  const guardAction = (event: Event) => {
    if (pausedTarget(event)?.closest(ACTION_CONTROL)) stopAction(event);
  };
  const guardSubmit = (event: Event) => {
    if (pausedTarget(event)) stopAction(event);
  };
  const guardKeyboard = (event: KeyboardEvent) => {
    const target = pausedTarget(event);
    if (!target || event.isComposing || event.keyCode === 229) return;
    if (target.closest(ACTION_CONTROL) && (event.key === 'Enter' || event.key === ' ')) {
      stopAction(event);
      return;
    }
    if (event.key !== 'Enter' || event.shiftKey) return;
    const editor = target.closest('input, textarea, [contenteditable="true"], [role="textbox"]');
    if (!editor) return;
    // O Enter continua inserindo uma linha em editores multilinha; os handlers
    // de envio não recebem o evento. Campos de uma linha não submetem o form.
    if (editor.matches('input')) event.preventDefault();
    event.stopImmediatePropagation();
  };

  // Captura antes da delegação React, inclusive para modais renderizados em portal.
  const actionEvents = ['pointerdown', 'mousedown', 'click'] as const;
  const keyboardEvents = ['keydown', 'keypress', 'keyup'] as const;
  actionEvents.forEach((type) => window.addEventListener(type, guardAction, true));
  window.addEventListener('touchstart', guardAction, { capture: true, passive: false });
  keyboardEvents.forEach((type) => window.addEventListener(type, guardKeyboard, true));
  window.addEventListener('submit', guardSubmit, true);
  return () => {
    actionEvents.forEach((type) => window.removeEventListener(type, guardAction, true));
    window.removeEventListener('touchstart', guardAction, true);
    keyboardEvents.forEach((type) => window.removeEventListener(type, guardKeyboard, true));
    window.removeEventListener('submit', guardSubmit, true);
  };
}

/** Aviso compartilhado: preserva as rotas e a edição em andamento. */
const PlatformAvailabilityNotice: React.FC = () => {
  const { isDark } = useTheme();
  const availability = useSyncExternalStore(
    subscribePlatformAvailability,
    getPlatformAvailabilitySnapshot,
    getPlatformAvailabilitySnapshot,
  );

  useEffect(() => startPlatformAvailabilityMonitoring(), []);
  useEffect(() => startPlatformInteractionGuard(), []);

  const reconnecting = availability.state !== 'available';
  const pendingConfirmation = availability.pendingCommands > 0;
  const visible = reconnecting || pendingConfirmation;

  return (
    <div
      data-platform-availability-notice="true"
      className="pointer-events-none fixed z-[10010] w-auto max-w-lg"
      style={{
        top: 'max(0.75rem, env(safe-area-inset-top))',
        right: 'max(0.75rem, env(safe-area-inset-right))',
        left: 'max(0.75rem, env(safe-area-inset-left))',
        marginLeft: 'auto',
      }}
    >
      <div role="status" aria-live="polite" aria-atomic="true">
        {visible && (
          <div className={`pointer-events-auto rounded-2xl border p-4 shadow-xl ${
            isDark
              ? 'border-gray-600 bg-gray-800 text-gray-200'
              : 'border-gray-200 bg-white text-gray-800'
          }`}>
            <div className="flex items-start gap-3">
              {reconnecting ? (
                <Loader2 aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 motion-safe:animate-spin text-sminy-primary" />
              ) : (
                <AlertCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-sminy-primary" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {reconnecting
                    ? 'Reconectando, aguarde alguns segundos.'
                    : 'Conexão restabelecida. Confira a última ação.'}
                </p>
                <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {pendingConfirmation
                    ? 'Uma ação ficou sem confirmação. Confira se ela foi concluída antes de tentar novamente.'
                    : 'Mantenha esta tela aberta. Aguarde a conexão voltar para salvar ou enviar.'}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {reconnecting ? (
                    <button
                      type="button"
                      disabled={availability.state === 'recovering'}
                      onClick={() => { void checkPlatformAvailability().catch(() => undefined); }}
                      className={`min-h-[44px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${
                        isDark
                          ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus-visible:ring-offset-gray-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-offset-white'
                      }`}
                    >
                      Verificar conexão
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={acknowledgePlatformPendingCommands}
                      className={`min-h-[44px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 ${
                        isDark
                          ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus-visible:ring-offset-gray-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-offset-white'
                      }`}
                    >
                      Já conferi
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAvailabilityNotice;
